import React from "react";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../../components/CustomTable/CustomTable";
import { Button } from "reactstrap";
import { t } from "i18next";
import { usePagination } from "../../../hooks/usePagination";
import PaginationHandler from "../../../components/PaginationHandler";
import { getLogs, deleteLog } from "../../../api/log";
import axios from "axios";

export default function DealLogs({ dealId }) {
  const navigate = useNavigate();
  localStorage.setItem("dealId", dealId);
  const currentUser = JSON.parse(localStorage.getItem("currentUser")) || {};
  const role = currentUser.role || currentUser.roleName || {};
  const token = localStorage.getItem("token");
  const hasPermission = (action) => {
    if (role.name === "super admin") return true;
    return (
      role.permissions &&
      role.permissions.some(
        (perm) => perm.resource === "Sales" && perm.actions.includes(action)
      )
    );
  };

  const canEdit = () => hasPermission("update");
  const canDelete = () => hasPermission("delete");
  const canRead = () => hasPermission("update") || hasPermission("delete");
  const { response, loading, error, setPage, page, totalPages } = usePagination(
    getLogs,
    { dealId },
    Boolean(dealId)
  );

  const handleDelete = async (id) => {
    if (window.confirm(t("Are you sure you want to delete this log?"))) {
      try {
        await deleteLog(id);
        setPage(1);
      } catch (err) {
        console.error("Delete failed:", err);
        alert(t("Failed to delete log."));
      }
    }
  };

  const handleDownload = async (fileUrl) => {
    try {
      // Ensure we have a proper URL format
      const normalizedUrl = fileUrl.replace(/\\/g, "/");
      const encodedUrl = encodeURIComponent(normalizedUrl);

      const response = await axios.get(
        `${process.env.REACT_APP_AXIOS}logs/download/file?url=${encodedUrl}`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Check if we actually got a file
      if (response.data.size === 0) {
        throw new Error("Received empty file");
      }

      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", normalizedUrl.split("/").pop());
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Download failed:", error);
      // alert(
      //   error.response?.data?.message ||
      //     t("Failed to download file. Please try again.")
      // );
    }
  };

  if (!canRead()) {
    return <div>{t("You do not have permission to view logs.")}</div>;
  }

  return (
    <div className="mt-4">
      {response && response.length > 0 && !loading ? (
        <>
          <CustomTable
            emptyMsg={t("No logs found.")}
            data={response}
            loading={loading}
            error={error}
            cols={[
              {
                label: t("Delivered"),
                key: "delivered",
                style: { width: "15%" },
                renderer: (row) => (row.delivered ? "✅" : "❌"),
              },
              {
                label: t("Pending"),
                key: "pending",
                style: { width: "15%" },
                renderer: (row) => (row.pending ? "✅" : "❌"),
              },
              {
                label: t("Notes"),
                key: "notes",
                style: { width: "20%" },
                renderer: (row) => row.notes || t("No notes"),
              },
              {
                label: t("Files"),
                key: "files",
                style: { width: "30%" },
                renderer: (row) => {
                  if (!row.files || row.files.length === 0) {
                    return t("No files");
                  }

                  return (
                    <div className="d-flex flex-column gap-1">
                      {row.files.map((file, idx) => (
                        <a
                          key={idx}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDownload(file);
                          }}
                          className="text-primary text-decoration-none text-truncate"
                          style={{
                            maxWidth: "100%",
                            display: "inline-block",
                            cursor: "pointer",
                          }}
                          title={file.split("/").pop()}
                        >
                          {file.split("/").pop()}
                        </a>
                      ))}
                    </div>
                  );
                },
              },
              {
                label: t("Actions"),
                key: "actions",
                style: { width: "15%" },
                renderer: (row) => (
                  <div className="d-flex gap-2">
                    {canEdit() && (
                      <Button
                        color="info"
                        size="sm"
                        onClick={() => navigate(`/logs/edit/${row._id}`)}
                      >
                        {t("Edit")}
                      </Button>
                    )}
                    {canDelete() && (
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleDelete(row._id)}
                      >
                        {t("Delete")}
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
          />

          <PaginationHandler
            page={page}
            setPage={setPage}
            totalPages={totalPages}
          />
        </>
      ) : (
        !loading && <div>{t("No results found.")}</div>
      )}
    </div>
  );
}
